import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CourseFeatures() {
  const features = [
    {
      icon: 'ri-play-circle-line',
      title: 'Lesson Content',
      description: 'Structured lessons covering Country connection, cultural protocols, engagement, ICIP, and policy-to-practice.',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Case Studies and Examples',
      description: 'Realistic NSW project scenarios that demonstrate Country-centred decision-making.',
    },
    {
      icon: 'ri-download-line',
      title: 'Downloadable Resources',
      description: 'Templates, checklists, and guidance notes to support culturally safe practice and delivery.',
    },
    {
      icon: 'ri-question-answer-line',
      title: 'Reflection Activities',
      description: 'Short prompts to help teams apply learning to their project context and improve practice over time.',
    },
    {
      icon: 'ri-team-line',
      title: 'Team Access Options',
      description: 'Flexible access for organisations, with staged onboarding if needed.',
    },
    {
      icon: 'ri-bookmark-line',
      title: '12-Month Access (Self-Paced)',
      description: 'Learn at your own pace and revisit key lessons anytime during your access period.',
    },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: footerRef, isVisible: footerVisible } = useScrollAnimation({ threshold: 0.5 });

  return (
    <section id="course-features" className="py-20 relative overflow-hidden" style={{ backgroundColor: '#FFFAF5' }}>
      {/* Dot pattern overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #E4852355 1.5px, transparent 1.5px)',
          backgroundSize: '22px 22px',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          opacity: 0.45,
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#E48523]/10 text-[#BA3D03] rounded-full text-sm font-semibold mb-5">
            Course Features
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D1203] mb-4">
            What's Included in Your Access
          </h2>
          <p className="text-lg text-[#3D1203]/70 max-w-2xl mx-auto">
            Practical learning materials designed for NSW built environment teams to apply in real projects.
          </p>
        </div>

        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-[#FFF9F5] rounded-xl p-7 hover:shadow-md transition-all duration-500 border border-[#E8C580]/30 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: gridVisible ? `${index * 100}ms` : '0ms' }}
            >
              <div className="w-12 h-12 bg-[#E48523] rounded-lg flex items-center justify-center mb-5">
                <i className={`${feature.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-lg font-bold text-[#3D1203] mb-2">{feature.title}</h3>
              <p className="text-sm text-[#3D1203]/70 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <p
          ref={footerRef as React.RefObject<HTMLParagraphElement>}
          className={`text-center text-sm text-[#3D1203]/50 mt-8 transition-all duration-700 ease-out ${
            footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="font-semibold text-[#3D1203]/70">Support:</span> Email support for access issues and course enquiries during your access period.
        </p>
      </div>
    </section>
  );
}
